import React, { useState, useEffect, useRef } from 'react';
import { 
  User, Package, Heart, MapPin, CreditCard, Bell, Shield, 
  Gift, Star, Clock, Gem, Ruler, Sparkles, ChevronRight, 
  Edit3, Camera, Save, Phone, Mail, Calendar, LogOut, Lock, Settings, QrCode, ShoppingBag,
  Search, Filter, Truck, FileText, ChevronDown, ChevronUp, CheckCircle2,
  Trash2, Minus, Plus, CheckSquare, Square, X, DollarSign, Image as ImageIcon, Upload, Eye,
  RotateCcw, Download, Store, Loader,
  XCircle, HelpCircle
} from 'lucide-react';
import { User as UserType, WishlistItem, Product, MemberOrder } from '../types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface MemberProfileProps {
  user: UserType | null;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  onUpdateUser?: (updatedUser: UserType) => void;
  initialTab?: 'overview' | 'orders' | 'wishlist' | 'settings' | 'payment-proof';
  wishlist?: WishlistItem[];
  onUpdateWishlistQuantity?: (id: number, delta: number) => void;
  onToggleWishlistSelection?: (id: number) => void;
  onToggleAllWishlistSelection?: (select: boolean) => void;
  onRemoveFromWishlist?: (ids: number[]) => void;
  onMoveToCart?: (ids: number[]) => void;
  onSelectProduct?: (product: Product) => void;
  orders?: MemberOrder[];
  onUpdateOrder?: (orderId: string, updates: Partial<MemberOrder>) => void;
}

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

// More reliable placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/300x300/f5f5f5/999999?text=No+Image';

// Helper function to extract numeric ID from order ID
const extractNumericId = (orderId: string): number => {
  if (typeof orderId === 'string') {
    const numericPart = orderId.replace(/[^0-9]/g, '');
    const result = parseInt(numericPart, 10);
    return isNaN(result) ? 0 : result;
  }
  const result = parseInt(orderId, 10);
  return isNaN(result) ? 0 : result;
};

// Helper function to get absolute URL for images
const getAbsoluteUrl = (url: string | null | undefined): string => {
  if (!url) return PLACEHOLDER_IMAGE;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('/uploads/')) return `${window.location.origin}${url}`;
  if (url.startsWith('/api/uploads/')) return `${window.location.origin}${url}`;
  if (url.startsWith('uploads/')) return `${window.location.origin}/${url}`;
  return url;
};

// Helper function to get display status (maps 'Processing' to 'Preparing')
const getDisplayStatus = (status: string | undefined): string => {
  if (!status) return 'Pending';
  if (status === 'Processing') return 'Preparing';
  return status;
};

// Helper function to check if order is complete (Delivered or Picked Up)
const isOrderComplete = (status: string | undefined): boolean => {
  if (!status) return false;
  return status === 'Delivered' || status === 'Picked Up';
};

// Helper function to get status style class
const getStatusStyle = (status: string | undefined): string => {
  if (!status) return 'bg-orange-50 text-orange-700 border-orange-200';
  
  const isComplete = isOrderComplete(status);
  
  if (isComplete) {
    return 'bg-green-100 text-green-700 border-green-200';
  }
  
  const displayStatus = getDisplayStatus(status);
  
  const styles: Record<string, string> = {
    'Shipped': 'bg-blue-50 text-blue-700 border-blue-200',
    'Preparing': 'bg-purple-50 text-purple-700 border-purple-200',
    'Paid': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Ready for Pickup': 'bg-amber-50 text-amber-700 border-amber-200',
    'Pending': 'bg-orange-50 text-orange-700 border-orange-200'
  };
  
  return styles[displayStatus] || 'bg-orange-50 text-orange-700 border-orange-200';
};

// Helper function to get status icon
const getStatusIcon = (status: string | undefined) => {
  if (!status) return <Clock size={14} />;
  
  const isComplete = isOrderComplete(status);
  const displayStatus = getDisplayStatus(status);
  
  if (isComplete) {
    return <CheckCircle2 size={14} className="text-green-600" />;
  }
  
  switch (displayStatus) {
    case 'Delivered':
      return <CheckCircle2 size={14} />;
    case 'Shipped':
      return <Truck size={14} />;
    case 'Preparing':
      return <Package size={14} />;
    case 'Paid':
      return <CheckCircle2 size={14} />;
    case 'Ready for Pickup':
      return <Store size={14} />;
    default:
      return <Clock size={14} />;
  }
};

// Helper function to get status display text
const getStatusDisplayText = (status: string | undefined): string => {
  if (!status) return 'Pending';
  if (status === 'Delivered' || status === 'Picked Up') return 'Order Complete';
  if (status === 'Processing') return 'Preparing';
  return status;
};

// Tier configuration - realistic points system (100 PHP = 1 point)
const TIER_CONFIG = {
  Sapphire: {
    minPoints: 0,
    maxPoints: 4999,
    nextTier: 'Ruby',
    color: 'from-blue-600 to-blue-400',
    badgeColor: 'bg-blue-600',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    bgLight: 'bg-blue-50',
    icon: '💎',
    benefits: ['Welcome points', 'Basic concierge', 'Birthday rewards']
  },
  Ruby: {
    minPoints: 5000,
    maxPoints: 14999,
    nextTier: 'Emerald',
    color: 'from-red-600 to-red-400',
    badgeColor: 'bg-red-600',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    bgLight: 'bg-red-50',
    icon: '❤️',
    benefits: ['5% discount', 'Priority support', 'Early access sales', 'Exclusive events']
  },
  Emerald: {
    minPoints: 15000,
    maxPoints: 49999,
    nextTier: 'Diamond',
    color: 'from-emerald-600 to-emerald-400',
    badgeColor: 'bg-emerald-600',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    bgLight: 'bg-emerald-50',
    icon: '✨',
    benefits: ['10% discount', 'Free shipping', 'Private viewings', 'Personal shopper']
  },
  Diamond: {
    minPoints: 50000,
    maxPoints: Infinity,
    nextTier: null,
    color: 'from-cyan-500 to-blue-500',
    badgeColor: 'bg-gradient-to-r from-cyan-500 to-blue-500',
    textColor: 'text-cyan-600',
    borderColor: 'border-cyan-200',
    bgLight: 'bg-cyan-50',
    icon: '👑',
    benefits: ['15% discount', 'VIP events', 'Personal stylist', 'Complimentary insurance', 'Priority reservations']
  }
};

// Calculate points from total spent (100 PHP = 1 point)
const calculatePointsFromSpent = (totalSpent: number): number => {
  return Math.floor(totalSpent / 100);
};

// Get tier based on points
const getTierFromPoints = (points: number) => {
  if (points >= 50000) return 'Diamond';
  if (points >= 15000) return 'Emerald';
  if (points >= 5000) return 'Ruby';
  return 'Sapphire';
};

// Get tier details
const getTierDetails = (points: number) => {
  const tierName = getTierFromPoints(points);
  const config = TIER_CONFIG[tierName as keyof typeof TIER_CONFIG];
  const nextTierConfig = config.nextTier ? TIER_CONFIG[config.nextTier as keyof typeof TIER_CONFIG] : null;
  
  const pointsToNext = nextTierConfig ? nextTierConfig.minPoints - points : 0;
  const spendToNext = pointsToNext * 100;
  const progress = nextTierConfig 
    ? ((points - config.minPoints) / (nextTierConfig.minPoints - config.minPoints)) * 100 
    : 100;
  
  return {
    tierName,
    config,
    nextTierName: config.nextTier,
    pointsToNext,
    spendToNext,
    progress: Math.min(100, Math.max(0, progress))
  };
};

const MemberProfile: React.FC<MemberProfileProps> = ({ 
    user, 
    onNavigate, 
    onLogout, 
    onUpdateUser,
    initialTab = 'overview',
    wishlist = [],
    onUpdateWishlistQuantity,
    onToggleWishlistSelection,
    onToggleAllWishlistSelection,
    onRemoveFromWishlist,
    onMoveToCart,
    onSelectProduct,
    orders = [],
    onUpdateOrder
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'wishlist' | 'settings' | 'payment-proof'>(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  
  // Tier Info Modal State
  const [showTierInfoModal, setShowTierInfoModal] = useState(false);
  
  // Order History State
  const [orderFilter, setOrderFilter] = useState('All');
  const [orderSearch, setOrderSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  
  // Tracking Popup State
  const [trackingModalOrder, setTrackingModalOrder] = useState<MemberOrder | null>(null);
  const [orderTimeline, setOrderTimeline] = useState<any[]>([]);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Payment Submission Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
      orderId: '',
      transactionRef: '',
      proofPreview: null as string | null,
      isUploading: false
  });

  // Profile Photo Upload State - INCREASED to 20MB
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);

  // Image error states
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [wishlistImageErrors, setWishlistImageErrors] = useState<Record<number, boolean>>({});

  // Profile Data State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: '',
    address: '',
    city: '',
    postalCode: '',
    avatar: PLACEHOLDER_IMAGE
  });

  // Mock Data (will be replaced with real data from API)
  const [memberSince, setMemberSince] = useState("October 2023");
  const [tier, setTier] = useState("Sapphire");
  const [points, setPoints] = useState(1250);
  const nextTierPoints = 5000;

  // Calculate real points from orders
  const totalSpent = orders.reduce((sum, order) => sum + (order.totalAmount || order.total || 0), 0);
  const calculatedPoints = calculatePointsFromSpent(totalSpent);
  const tierDetails = getTierDetails(calculatedPoints);

  // Member since date
  const memberSinceDate = user?.created_at 
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : (orders.length > 0 && orders[0]?.orderDate 
      ? new Date(orders[0].orderDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : 'March 2026');

  // Member ID
  const memberId = user?.id ? `SG-${new Date().getFullYear()}-${String(user.id).padStart(4, '0')}` : 'SG-2026-0001';

  // Lightbox functions
  const openLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  // Load user data when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      loadUserProfile();
    } else {
      setProfileData({
        firstName: user?.name?.split(' ')[0] || 'Guest',
        lastName: user?.name?.split(' ').slice(1).join(' ') || 'User',
        email: user?.email || 'member@sparklinggems.ph',
        phone: user?.phone || '+63 900 000 0000',
        birthday: '',
        address: 'Metro Manila, Philippines',
        city: 'Makati',
        postalCode: '1200',
        avatar: user?.avatar || PLACEHOLDER_IMAGE
      });
    }
  }, [user]);
 
  const loadUserProfile = async () => {
    if (!user?.id) {
      setProfileLoadError('User ID not available');
      return;
    }
    
    setIsLoadingProfile(true);
    setProfileLoadError(null);
    
    try {
      console.log('Loading profile for user ID:', user.id);
      const response = await fetch(`${API_BASE_URL}/users/get-profile.php?user_id=${user.id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Profile load response:', data);
      
      if (data.success && data.user) {
        const avatarUrl = getAbsoluteUrl(data.user.avatar);
        
        setProfileData({
          firstName: data.user.firstName || data.user.first_name || '',
          lastName: data.user.lastName || data.user.last_name || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          birthday: data.user.birthday || '',
          address: data.user.address || '',
          city: data.user.city || '',
          postalCode: data.user.postalCode || data.user.postal_code || '',
          avatar: avatarUrl
        });
        
        if (data.user.membership_tier) setTier(data.user.membership_tier);
        if (data.user.points) setPoints(data.user.points);
        if (data.user.created_at) {
          const date = new Date(data.user.created_at);
          setMemberSince(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }
        
        console.log('Profile loaded successfully');
      } else {
        console.log('Using fallback profile data');
        setProfileData({
          firstName: user?.name?.split(' ')[0] || 'Guest',
          lastName: user?.name?.split(' ').slice(1).join(' ') || 'User',
          email: user?.email || 'member@sparklinggems.ph',
          phone: user?.phone || '+63 900 000 0000',
          birthday: '',
          address: 'Metro Manila, Philippines',
          city: 'Makati',
          postalCode: '1200',
          avatar: getAbsoluteUrl(user?.avatar) || PLACEHOLDER_IMAGE
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setProfileLoadError(error instanceof Error ? error.message : 'Failed to load profile');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Update URL when tab changes
  useEffect(() => {
    const tabMap: Record<string, string> = {
      overview: 'profile',
      orders: 'orders',
      wishlist: 'wishlist',
      settings: 'settings',
      'payment-proof': 'upload-proof'
    };
    
    const path = tabMap[activeTab] || 'profile';
    window.history.pushState(null, '', `/${path}`);
  }, [activeTab]);

  // Fetch timeline when tracking modal opens
  useEffect(() => {
    if (trackingModalOrder) {
      fetchTimeline(trackingModalOrder.id);
    }
  }, [trackingModalOrder]);

  const fetchTimeline = async (orderId: string) => {
    setIsLoadingTimeline(true);
    setOrderDetails(null);
    try {
      const numericId = extractNumericId(orderId);
      
      if (!numericId) {
        console.error('Could not extract numeric ID from:', orderId);
        setOrderTimeline([]);
        setIsLoadingTimeline(false);
        return;
      }
      
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_BASE_URL}/orders/get-timeline.php?order_id=${numericId}&_=${timestamp}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const sortedTimeline = (data.timeline || []).sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setOrderTimeline(sortedTimeline);
        
        if (data.order) {
          setOrderDetails(data.order);
        }
      } else {
        setOrderTimeline([]);
      }
    } catch (error) {
      console.error('Error fetching timeline:', error);
      setOrderTimeline([]);
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (orders.length > 0) {
      console.log('Orders in MemberProfile:', orders.length);
    }
  }, [orders]);

  const pendingOrders = orders.filter(o => o && o.status === 'Pending');

  // Handle profile save with API call
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      alert('User not logged in');
      return;
    }
    
    setIsUploadingPhoto(true);
    setSaveSuccess(false);
    
    try {
      const payload = {
        user_id: user.id,
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        city: profileData.city || '',
        postalCode: profileData.postalCode || ''
      };
      
      const response = await fetch(`${API_BASE_URL}/users/update-profile.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Server returned invalid response');
      }
      
      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        alert('Profile updated successfully!');
        
        if (onUpdateUser && user) {
          const updatedUser: UserType = {
            ...user,
            name: `${profileData.firstName} ${profileData.lastName}`.trim(),
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            email: profileData.email,
            phone: profileData.phone
          };
          onUpdateUser(updatedUser);
        }
        
        await loadUserProfile();
        setIsEditing(false);
      } else {
        alert('Failed to update profile: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(error instanceof Error ? error.message : 'Error saving profile. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Profile Photo Upload Handlers - UPDATED to 20MB
  const handlePhotoClick = () => {
    setShowPhotoUploadModal(true);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Increased file size limit to 20MB
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        alert('File too large. Maximum 20MB allowed.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto || !user?.id) {
      alert('No photo selected or user not logged in');
      return;
    }
    
    setIsUploadingPhoto(true);
    
    try {
      const response = await fetch(selectedPhoto);
      const blob = await response.blob();
      
      // Increased file size limit to 20MB
      if (blob.size > 20 * 1024 * 1024) {
        alert('File too large. Please use a smaller image (max 20MB).');
        setIsUploadingPhoto(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('user_id', user.id.toString());
      formData.append('avatar', blob, 'avatar.jpg');
      
      const uploadResponse = await fetch(`${API_BASE_URL}/users/update-avatar.php`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) {
        if (uploadResponse.status === 413) {
          throw new Error('File too large. Please use a smaller image (max 20MB).');
        }
        const errorText = await uploadResponse.text();
        throw new Error(`HTTP error! status: ${uploadResponse.status}`);
      }
      
      const result = await uploadResponse.json();
      
      if (result.success) {
        const avatarUrl = getAbsoluteUrl(result.avatar_url);
        setProfileData(prev => ({ ...prev, avatar: avatarUrl }));
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        alert("Profile photo updated successfully!");
        
        if (onUpdateUser && user) {
          const updatedUser: UserType = {
            ...user,
            avatar: avatarUrl
          };
          onUpdateUser(updatedUser);
        }
        
        setSelectedPhoto(null);
        setShowPhotoUploadModal(false);
        await loadUserProfile();
      } else {
        alert("Failed to upload photo: " + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error instanceof Error ? error.message : "Error uploading photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // ============================================================
  // Payment Modal Handlers - Direct API Upload
  // ============================================================
  const openPaymentModal = () => {
      setPaymentForm({
          orderId: pendingOrders.length > 0 ? pendingOrders[0]?.id || '' : '',
          transactionRef: '',
          proofPreview: null,
          isUploading: false
      });
      setShowPaymentModal(true);
  };

  const handleModalFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          if (file.size > 5 * 1024 * 1024) {
            alert('File too large. Maximum 5MB allowed.');
            return;
          }
          
          if (!file.type.startsWith('image/')) {
            alert('Please upload an image file.');
            return;
          }
          
          const url = URL.createObjectURL(file);
          setPaymentForm(prev => ({ ...prev, proofPreview: url }));
      }
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!paymentForm.orderId || !paymentForm.proofPreview || !paymentForm.transactionRef) {
          alert('Please fill in all fields');
          return;
      }
      
      setPaymentForm(prev => ({ ...prev, isUploading: true }));
      
      try {
          const response = await fetch(paymentForm.proofPreview);
          const blob = await response.blob();
          
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
          });
          
          const paymentProofBase64 = await base64Promise;
          const numericOrderId = extractNumericId(paymentForm.orderId);
          const userId = user?.id || null;
          
          console.log('Uploading payment proof for order:', numericOrderId);
          
          const uploadResponse = await fetch(`${API_BASE_URL}/orders/upload-proof.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  order_id: numericOrderId,
                  payment_proof: paymentProofBase64,
                  transaction_id: paymentForm.transactionRef,
                  user_id: userId
              })
          });
          
          const result = await uploadResponse.json();
          console.log('Upload result:', result);
          
          if (result.success) {
              alert('Payment proof submitted successfully! Our team will verify it shortly.');
              setShowPaymentModal(false);
              
              if (onUpdateOrder && paymentForm.orderId) {
                  onUpdateOrder(paymentForm.orderId, {
                      paymentProof: paymentProofBase64,
                      transactionId: paymentForm.transactionRef
                  });
              }
              
              setTimeout(() => {
                  window.location.reload();
              }, 1500);
          } else {
              alert('Failed to upload proof: ' + (result.error || 'Unknown error'));
          }
      } catch (error) {
          console.error('Error uploading proof:', error);
          alert('Failed to upload proof. Please try again.');
      } finally {
          setPaymentForm(prev => ({ ...prev, isUploading: false }));
      }
  };

  const handleProofUpload = async (e: React.ChangeEvent<HTMLInputElement>, orderId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
        alert('File too large. Maximum 5MB allowed.');
        return;
    }
    
    if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
    }
    
    try {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        
        const paymentProofBase64 = await base64Promise;
        const numericOrderId = extractNumericId(orderId);
        const userId = user?.id || null;
        const transactionId = `TXN-${Date.now().toString().slice(-8)}-${Math.floor(Math.random() * 1000)}`;
        
        console.log('Uploading proof for order:', numericOrderId);
        
        const uploadResponse = await fetch(`${API_BASE_URL}/orders/upload-proof.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                order_id: numericOrderId,
                payment_proof: paymentProofBase64,
                transaction_id: transactionId,
                user_id: userId
            })
        });
        
        const result = await uploadResponse.json();
        console.log('Upload result:', result);
        
        if (result.success) {
            alert('Receipt uploaded successfully. Status will be updated after admin review.');
            
            if (onUpdateOrder) {
                onUpdateOrder(orderId, {
                    paymentProof: paymentProofBase64,
                    transactionId: transactionId
                });
            }
            
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } else {
            alert('Failed to upload proof: ' + (result.error || 'Unknown error'));
        }
    } catch (error) {
        console.error('Error uploading proof:', error);
        alert('Failed to upload proof. Please try again.');
    }
    
    e.target.value = '';
  };

  // Filter Orders
  const filteredOrders = orders.filter(order => {
      if (!order) return false;
      
      const orderIdStr = order.id ? String(order.id).toLowerCase() : '';
      const searchLower = orderSearch.toLowerCase();
      
      const matchesId = orderIdStr.includes(searchLower);
      
      const matchesItem = order.items && order.items.some(item => 
          item && item.name && item.name.toLowerCase().includes(searchLower)
      );
      
      const matchesSearch = matchesId || matchesItem;
      const matchesFilter = orderFilter === 'All' || (order.status && order.status === orderFilter);
      
      return matchesSearch && matchesFilter;
  });

  const toggleOrderExpand = (id: string) => {
      setExpandedOrder(expandedOrder === id ? null : id);
  };

  const downloadInvoice = (order: MemberOrder) => {
    try {
      const orderAny = order as any;
      const doc = new jsPDF();
      
      doc.setFontSize(22);
      doc.setFont('times', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Sparkling Gems', 105, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('INVOICE', 105, 30, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order #: ${orderAny.order_number || order.id}`, 20, 45);
      
      const orderDate = order.orderDate ? new Date(order.orderDate).toLocaleString('en-PH') : 
                       (order.date ? new Date(order.date).toLocaleString('en-PH') : 
                       new Date().toLocaleString('en-PH'));
      doc.text(`Date: ${orderDate}`, 20, 52);
      
      doc.setFontSize(11);
      doc.text('Bill To:', 20, 65);
      doc.setFontSize(10);
      
      const customerName = order.customerName || orderAny.customer_name || `${profileData.firstName} ${profileData.lastName}`;
      doc.text(customerName, 20, 72);
      
      const customerEmail = orderAny.customer_email || profileData.email;
      doc.text(customerEmail, 20, 79);
      
      const customerPhone = orderAny.customer_phone || profileData.phone;
      doc.text(customerPhone, 20, 86);
      
      const isPickup = orderAny.deliveryType === 'pickup' || orderAny.delivery_type === 'pickup';
      
      doc.text('Ship To:', 120, 65);
      let shippingAddress = '';
      if (isPickup) {
        shippingAddress = 'Store Pickup - Sparkling Gems Jewelry Shop\n2F, San Antonio Building, Corumi, Quezon City';
      } else {
        shippingAddress = order.shippingAddress || orderAny.shipping_address || 
                         `${profileData.address}, ${profileData.city} ${profileData.postalCode}`;
      }
      doc.text(shippingAddress, 120, 72, { maxWidth: 70 });
      
      const tableColumn = ["Item", "Qty", "Price", "Total"];
      const tableRows: any[][] = [];
      
      if (order.items && order.items.length > 0) {
        order.items.forEach((item: any) => {
          const itemData = [
            item.name || 'Product',
            item.quantity?.toString() || '1',
            `₱${(item.price || 0).toLocaleString()}`,
            `₱${((item.price || 0) * (item.quantity || 1)).toLocaleString()}`
          ];
          tableRows.push(itemData);
        });
      } else {
        tableRows.push(['No items', '-', '-', '₱0']);
      }
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 100,
        theme: 'striped',
        headStyles: { fillColor: [180, 148, 92] }
      });
      
      const finalY = (doc as any).lastAutoTable?.finalY + 10 || 150;
      
      const subtotal = order.subtotal || 0;
      const total = order.total || order.totalAmount || 0;
      
      doc.text(`Subtotal: ₱${subtotal.toLocaleString()}`, 140, finalY);
      doc.text(`Shipping: FREE`, 140, finalY + 7);
      doc.setFontSize(12);
      doc.setFont('times', 'bold');
      doc.text(`Total: ₱${total.toLocaleString()}`, 140, finalY + 17);
      
      doc.setFontSize(10);
      doc.setFont('times', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Payment Method: ${order.paymentMethod || orderAny.payment_method || 'Bank Transfer'}`, 20, finalY + 10);
      
      const transactionId = order.transactionId || orderAny.transaction_id || 'N/A';
      doc.text(`Transaction ID: ${transactionId}`, 20, finalY + 17);
      doc.text(`Status: ${getStatusDisplayText(order.status)}`, 20, finalY + 24);
      
      doc.save(`invoice-${order.id}.pdf`);
      alert('Invoice downloaded successfully!');
      
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice. Please try again.');
    }
  };

  const selectedWishlistItems = wishlist.filter(item => item && item.selected);
  const isAllSelected = wishlist.length > 0 && selectedWishlistItems.length === wishlist.length;

  const handleBulkDelete = () => {
      if (onRemoveFromWishlist) {
          onRemoveFromWishlist(selectedWishlistItems.map(i => i.id));
      }
  };

  const handleBulkMoveToCart = () => {
      if (onMoveToCart) {
          onMoveToCart(selectedWishlistItems.map(i => i.id));
      }
  };

  const formatNumber = (value: number | undefined | null, defaultValue: string = '0'): string => {
      if (value === undefined || value === null || isNaN(value)) return defaultValue;
      try {
          return value.toLocaleString();
      } catch (e) {
          return defaultValue;
      }
  };

  const formatCurrency = (value: number | undefined | null): string => {
      if (value === undefined || value === null || isNaN(value)) return '₱0';
      try {
          return `₱${value.toLocaleString()}`;
      } catch (e) {
          return '₱0';
      }
  };

  const getProductImage = (item: any, itemId?: number): string => {
    if (!item) return PLACEHOLDER_IMAGE;
    if (itemId && imageErrors[`${itemId}`]) {
      return PLACEHOLDER_IMAGE;
    }
    
    const possibleImageFields = ['image', 'image_url', 'mainImage', 'productImage', 'img', 'photo', 'product_image'];
    for (const field of possibleImageFields) {
      if (item[field] && typeof item[field] === 'string' && item[field].trim() !== '') {
        return getAbsoluteUrl(item[field]);
      }
    }
    
    return PLACEHOLDER_IMAGE;
  };

  const handleImageError = (itemId: number) => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  const handleWishlistImageError = (itemId: number) => {
    setWishlistImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  const createProductFromItem = (item: any): Product => {
    return {
      id: item.product_id || item.id || 0,
      name: item.name || 'Product',
      description: item.description || item.name || 'Product',
      price: item.price || 0,
      image: getProductImage(item, item.id),
      category: item.category || 'jewelry',
      isNew: false,
      isBestSeller: false,
      condition: 'Brand New',
      weight: item.weight || '',
      karat: item.karat || '',
      goldType: item.goldType || item.gold_type || '',
      stock: item.stock || 0
    };
  };

  const getTimelineImageUrl = (event: any): string => {
    if (event.image_url) {
      return getAbsoluteUrl(event.image_url);
    }
    
    if (event.id && event.has_image) {
      return `${API_BASE_URL}/get-image.php?id=${event.id}`;
    }
    
    if (event.proof_image && !event.proof_image.startsWith('blob:')) {
      return getAbsoluteUrl(event.proof_image);
    }
    
    return PLACEHOLDER_IMAGE;
  };

  // Tier Info Modal Component
  const TierInfoModal = () => {
    const tiers = [
      { name: 'Sapphire', icon: '💎', minPoints: 0, maxPoints: 4999, minSpend: '₱0', maxSpend: '₱499,900', benefits: TIER_CONFIG.Sapphire.benefits },
      { name: 'Ruby', icon: '❤️', minPoints: 5000, maxPoints: 14999, minSpend: '₱500,000', maxSpend: '₱1,499,900', benefits: TIER_CONFIG.Ruby.benefits },
      { name: 'Emerald', icon: '✨', minPoints: 15000, maxPoints: 49999, minSpend: '₱1,500,000', maxSpend: '₱4,999,900', benefits: TIER_CONFIG.Emerald.benefits },
      { name: 'Diamond', icon: '👑', minPoints: 50000, maxPoints: '∞', minSpend: '₱5,000,000', maxSpend: '∞', benefits: TIER_CONFIG.Diamond.benefits }
    ];

    return (
      <div className="fixed inset-0 z-[500] flex items-center justify-center">
        <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" onClick={() => setShowTierInfoModal(false)}></div>
        <div className="relative bg-white w-full max-w-4xl rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[501] max-h-[90vh] overflow-y-auto">
          <div className="bg-stone-900 text-white p-6 flex justify-between items-center sticky top-0">
            <div>
              <h3 className="font-serif text-2xl">Loyalty Tiers & Benefits</h3>
              <p className="text-xs text-stone-400 mt-1">Earn points and unlock exclusive privileges</p>
            </div>
            <button onClick={() => setShowTierInfoModal(false)} className="text-stone-400 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>
          
          <div className="p-8">
            <div className="mb-8 bg-gold-50 p-6 rounded-sm border border-gold-200">
              <div className="flex items-center gap-3 mb-3">
                <Star size={24} className="text-gold-600" />
                <h4 className="font-bold text-stone-900 text-lg">How to Earn Points</h4>
              </div>
              <p className="text-stone-700">Earn <strong className="text-gold-600">1 point for every ₱100 spent</strong> on any purchase. Points never expire and can be redeemed for exclusive rewards, discounts, and special experiences.</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-stone-100">
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold">Tier</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold">Points Required</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold">Spend Required</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold">Benefits</th>
                   </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {tiers.map((tier, idx) => (
                    <tr key={idx} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{tier.icon}</span>
                          <span className="font-bold text-stone-900">{tier.name}</span>
                        </div>
                       </td>
                      <td className="px-4 py-4 text-stone-600">
                        {typeof tier.minPoints === 'number' ? tier.minPoints.toLocaleString() : tier.minPoints} - {typeof tier.maxPoints === 'number' ? tier.maxPoints.toLocaleString() : tier.maxPoints} pts
                       </td>
                      <td className="px-4 py-4 text-stone-600">
                        {tier.minSpend} - {tier.maxSpend}
                       </td>
                      <td className="px-4 py-4">
                        <ul className="space-y-1">
                          {tier.benefits.map((benefit, i) => (
                            <li key={i} className="text-xs text-stone-600 flex items-center gap-1">
                              <CheckCircle2 size={10} className="text-green-500" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-8 p-6 bg-stone-50 rounded-sm border border-stone-200">
              <h4 className="font-bold text-stone-900 mb-3">Your Current Progress</h4>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{tierDetails.tierName}</span>
                <span className="text-sm text-stone-500">{calculatedPoints.toLocaleString()} points</span>
              </div>
              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${tierDetails.config.color} rounded-full transition-all duration-500`}
                  style={{ width: `${tierDetails.progress}%` }}
                />
              </div>
              {tierDetails.nextTierName && (
                <p className="text-xs text-stone-500 mt-3">
                  ✨ {tierDetails.pointsToNext.toLocaleString()} more points needed to reach {tierDetails.nextTierName}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch(activeTab) {
        case 'overview':
            return (
                <div className="space-y-8 animate-fade-in">
                    {isLoadingProfile ? (
                        <div className="flex justify-center py-12">
                            <Loader size={32} className="animate-spin text-gold-600" />
                        </div>
                    ) : profileLoadError ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-sm text-center">
                            <p className="mb-4">{profileLoadError}</p>
                            <button 
                                onClick={loadUserProfile}
                                className="px-4 py-2 bg-red-600 text-white text-xs uppercase tracking-wider hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className={`p-3 rounded-full ${tierDetails.config.bgLight}`}>
                                            <Package size={20} className={tierDetails.config.textColor} />
                                        </div>
                                        <span className="text-2xl font-serif text-stone-900">{orders.length}</span>
                                    </div>
                                    <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">Total Orders</p>
                                    <p className="text-xs text-stone-400 mt-1">Lifetime purchases</p>
                                </div>
                                
                                <div className="bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className={`p-3 rounded-full ${tierDetails.config.bgLight}`}>
                                            <Heart size={20} className={tierDetails.config.textColor} />
                                        </div>
                                        <span className="text-2xl font-serif text-stone-900">{wishlist.length}</span>
                                    </div>
                                    <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">Wishlist Items</p>
                                    <p className="text-xs text-stone-400 mt-1">Saved for later</p>
                                </div>
                                
                                <div className="bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className={`p-3 rounded-full ${tierDetails.config.bgLight}`}>
                                            <Star size={20} className={tierDetails.config.textColor} />
                                        </div>
                                        <span className="text-2xl font-serif text-stone-900">{calculatedPoints.toLocaleString()}</span>
                                    </div>
                                    <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">Loyalty Points</p>
                                    <p className="text-xs text-stone-400 mt-1">100 PHP = 1 point</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="font-serif text-3xl text-stone-900">Welcome, {profileData.firstName || 'Member'}.</h2>
                                            <p className="text-stone-500 font-light text-sm mt-1">Manage your collection and concierge requests.</p>
                                        </div>
                                        <button 
                                            onClick={() => setShowTierInfoModal(true)}
                                            className="w-8 h-8 rounded-full bg-stone-100 hover:bg-gold-100 text-stone-500 hover:text-gold-600 transition-all flex items-center justify-center group"
                                            title="View tier information"
                                        >
                                            <HelpCircle size={18} className="group-hover:scale-110 transition-transform" />
                                        </button>
                                    </div>
                                    
                                    <div className={`bg-gradient-to-r ${tierDetails.config.color} p-6 rounded-sm shadow-lg text-white`}>
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <span className="text-3xl mr-2">{tierDetails.config.icon}</span>
                                                <span className="text-xs uppercase tracking-widest opacity-80">Current Tier</span>
                                                <h3 className="font-serif text-3xl mt-1">{tierDetails.tierName}</h3>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-2xl font-bold">{calculatedPoints.toLocaleString()}</p>
                                                <p className="text-xs opacity-80">Loyalty Points</p>
                                            </div>
                                        </div>
                                        
                                        {tierDetails.nextTierName && (
                                            <div className="mt-4">
                                                <div className="flex justify-between text-xs mb-2">
                                                    <span>{calculatedPoints.toLocaleString()} pts</span>
                                                    <span>{tierDetails.nextTierName}: {tierDetails.pointsToNext.toLocaleString()} more pts</span>
                                                    <span>{TIER_CONFIG[tierDetails.nextTierName as keyof typeof TIER_CONFIG].minPoints.toLocaleString()} pts</span>
                                                </div>
                                                <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-white rounded-full transition-all duration-1000 ease-out"
                                                        style={{ width: `${tierDetails.progress}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs mt-3 opacity-90">
                                                    ✨ Spend ₱ {tierDetails.spendToNext.toLocaleString()} more to reach {tierDetails.nextTierName}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {!tierDetails.nextTierName && (
                                            <div className="mt-4 pt-4 border-t border-white/30">
                                                <p className="text-sm font-medium">🏆 Maximum Tier Achieved!</p>
                                                <p className="text-xs opacity-80 mt-1">You've reached the highest tier. Enjoy exclusive Diamond benefits.</p>
                                            </div>
                                        )}
                                        
                                        <div className="mt-4 pt-4 border-t border-white/30">
                                            <p className="text-xs opacity-80 flex items-center gap-2">
                                                <span>{tierDetails.config.icon} {tierDetails.tierName} Benefits:</span>
                                                {tierDetails.config.benefits.slice(0, 2).join(' • ')}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-stone-50 p-4 rounded-sm border border-stone-200">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center">
                                                <Star size={16} className="text-gold-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-stone-900">How points work</p>
                                                <p className="text-[10px] text-stone-500">Earn 1 point for every ₱100 spent. Points never expire and can be redeemed for exclusive rewards.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="perspective-1000 group">
                                    <div className={`relative w-full aspect-[1.6/1] bg-gradient-to-br ${tierDetails.tierName === 'Diamond' ? 'from-stone-800 via-stone-900 to-black' : 'from-stone-800 to-stone-900'} rounded-xl shadow-2xl p-6 text-white overflow-hidden transition-transform duration-500 transform group-hover:rotate-y-6 group-hover:scale-105 border border-white/10`}>
                                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-20"></div>
                                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 z-0"></div>
                                        
                                        <div className="absolute top-4 right-4">
                                            <div className={`${tierDetails.config.badgeColor} text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg`}>
                                                <span>{tierDetails.config.icon}</span>
                                                <span>{tierDetails.tierName}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="relative z-10 flex flex-col justify-between h-full">
                                            <div className="flex items-center gap-2">
                                                <Gem className="text-gold-400" size={24} />
                                                <span className="font-serif text-lg tracking-wide">Sparkling Gems</span>
                                            </div>
                                            
                                            <div>
                                                <div className="mb-4">
                                                    <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Member Since</span>
                                                    <span className="text-sm font-light text-stone-300">{memberSinceDate}</span>
                                                </div>
                                                
                                                <div className="mb-4">
                                                    <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Member Name</span>
                                                    <span className="font-serif text-xl tracking-wide">{profileData.firstName} {profileData.lastName}</span>
                                                </div>
                                                
                                                <div>
                                                    <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Member ID</span>
                                                    <span className="font-mono text-sm text-stone-300">{memberId}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="absolute bottom-4 right-4 opacity-20">
                                                <QrCode size={48} className="text-white" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                <button 
                                    onClick={() => setActiveTab('orders')} 
                                    className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group"
                                >
                                    <div className="bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors">
                                        <Package size={22} />
                                    </div>
                                    <h3 className="font-bold text-stone-900 mb-1 group-hover:text-gold-600">Track Orders</h3>
                                    <p className="text-xs text-stone-500">View status of recent purchases</p>
                                </button>
                                
                                <button 
                                    onClick={() => setActiveTab('payment-proof')} 
                                    className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group"
                                >
                                    <div className="bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors">
                                        <Upload size={22} />
                                    </div>
                                    <h3 className="font-bold text-stone-900 mb-1 group-hover:text-gold-600">Upload Proof</h3>
                                    <p className="text-xs text-stone-500">Submit receipt for pending orders</p>
                                </button>

                                <button 
                                    onClick={() => onNavigate('contact')} 
                                    className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group"
                                >
                                    <div className="bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors">
                                        <Calendar size={22} />
                                    </div>
                                    <h3 className="font-bold text-stone-900 mb-1 group-hover:text-gold-600">Book Appointment</h3>
                                    <p className="text-xs text-stone-500">Schedule a private viewing</p>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            );

        case 'payment-proof':
            return (
                <div className="space-y-6 animate-fade-in">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-stone-200 pb-4 gap-4">
                        <div>
                            <h2 className="font-serif text-2xl text-stone-900">Payment Verification</h2>
                            <p className="text-xs text-stone-500 mt-1">Upload screenshots of bank transfers for manual verification.</p>
                        </div>
                        {pendingOrders.length > 0 && (
                            <button 
                                onClick={openPaymentModal}
                                className="bg-gold-600 text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-gold-500 transition-colors shadow-md flex items-center gap-2 rounded-sm"
                            >
                                <Upload size={14} /> Submit Payment Proof
                            </button>
                        )}
                    </div>

                    {pendingOrders.length > 0 ? (
                        <div className="grid grid-cols-1 gap-6">
                            {pendingOrders.map((order) => {
                                const orderAny = order as any;
                                const hasExistingProof = !!(order.paymentProof || order.payment_proof || order.file_path);
                                
                                return (
                                    <div key={order.id} className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm flex flex-col md:flex-row gap-8">
                                        <div className="flex-1 space-y-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold block mb-1">Order Ref</span>
                                                    <span className="font-mono text-lg text-stone-900">{orderAny.order_number || order.id}</span>
                                                    <p className="text-xs text-stone-500">{order.date || 'N/A'}</p>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold block mb-1">Amount Due</span>
                                                    <span className="font-serif text-xl text-stone-900">{formatCurrency(order.total)}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-stone-50 p-4 border border-stone-100 rounded-sm">
                                                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-2">Bank Details</p>
                                                <p className="text-sm font-bold text-stone-800">BPI: 1234-5678-90</p>
                                                <p className="text-xs text-stone-600">Sparkling Gems Inc.</p>
                                            </div>
                                        </div>

                                        <div className="md:w-1/3 flex flex-col justify-center">
                                            <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Proof of Payment</label>
                                            
                                            <div className="flex flex-col gap-3">
                                                {hasExistingProof ? (
                                                    <div 
                                                        className="relative group w-full h-32 bg-stone-100 border border-stone-200 rounded-sm overflow-hidden cursor-pointer"
                                                        onClick={() => openLightbox(getAbsoluteUrl(order.paymentProof || order.payment_proof || order.file_path))}
                                                    >
                                                        <img 
                                                            src={getAbsoluteUrl(order.paymentProof || order.payment_proof || order.file_path)} 
                                                            alt="Proof" 
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Eye size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-32 bg-stone-50 border-2 border-dashed border-stone-300 rounded-sm flex flex-col items-center justify-center text-stone-400">
                                                        <ImageIcon size={24} className="mb-2" />
                                                        <span className="text-[10px]">No image uploaded</span>
                                                    </div>
                                                )}

                                                <label className="cursor-pointer bg-stone-900 text-white w-full py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 rounded-sm shadow-sm text-center">
                                                    <Upload size={14} /> 
                                                    {hasExistingProof ? 'Replace Image' : 'Upload Receipt'}
                                                    <input 
                                                        type="file" 
                                                        className="hidden" 
                                                        accept="image/*"
                                                        onChange={(e) => handleProofUpload(e, order.id)}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-stone-50 rounded-sm border border-stone-100">
                            <CheckCircle2 className="mx-auto text-green-500 mb-6" size={48} />
                            <p className="text-stone-500 font-serif text-xl mb-2">All payments verified.</p>
                            <p className="text-stone-400 text-sm">You have no pending orders requiring proof of payment.</p>
                        </div>
                    )}
                </div>
            );

        case 'orders':
            return (
                <div className="space-y-6 animate-fade-in">
                    <div className="flex flex-col md:flex-row justify-between items-end border-b border-stone-200 pb-4 gap-4">
                        <div>
                             <h2 className="font-serif text-2xl text-stone-900">Order History</h2>
                             <p className="text-xs text-stone-500 mt-1">Track your orders and buy items again.</p>
                        </div>
                        
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                                <input 
                                    type="text" 
                                    placeholder="Search Order ID or Item" 
                                    value={orderSearch}
                                    onChange={(e) => setOrderSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none bg-white"
                                />
                            </div>
                            <div className="relative">
                                <select 
                                    value={orderFilter}
                                    onChange={(e) => setOrderFilter(e.target.value)}
                                    className="appearance-none pl-4 pr-8 py-2 text-sm border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none bg-white cursor-pointer"
                                >
                                    <option value="All">All Status</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Processing">Preparing</option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="Ready for Pickup">Ready for Pickup</option>
                                    <option value="Picked Up">Picked Up</option>
                                    <option value="Delivered">Delivered</option>
                                </select>
                                <Filter className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={14} />
                            </div>
                        </div>
                    </div>

                    {filteredOrders.length > 0 ? (
                        <div className="space-y-6">
                            {filteredOrders.map((order) => {
                                const orderAny = order as any;
                                const isPickup = orderAny.deliveryType === 'pickup' || 
                                                orderAny.delivery_type === 'pickup' || 
                                                (orderAny.shippingAddress && orderAny.shippingAddress.includes('Store Pickup'));
                                const isComplete = isOrderComplete(order.status);
                                
                                return (
                                    <div key={order.id} className={`bg-white border rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 ${isComplete ? 'border-green-200' : 'border-stone-200'}`}>
                                        <div className={`p-4 md:p-6 border-b flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${isComplete ? 'bg-green-50' : 'bg-stone-50'}`}>
                                            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                                                <div>
                                                    <span className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Order Placed</span>
                                                    <span className="text-sm font-bold text-stone-800">
                                                        {order.orderDate ? new Date(order.orderDate).toLocaleString('en-PH', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        }) : (order.date || 'N/A')}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Total Amount</span>
                                                    <span className="text-sm font-serif text-stone-800">{formatCurrency(order.totalAmount || order.total)}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Order No.</span>
                                                    <span className="text-sm font-mono text-stone-600">{orderAny.order_number || order.id}</span>
                                                </div>
                                                {isPickup && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm">
                                                        <Store size={12} className="text-amber-600" />
                                                        <span className="text-[9px] text-amber-700 font-medium">Pickup</span>
                                                    </div>
                                                )}
                                                {!isPickup && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-sm">
                                                        <Truck size={12} className="text-blue-600" />
                                                        <span className="text-[9px] text-blue-700 font-medium">Shipping</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button 
                                                    onClick={() => toggleOrderExpand(order.id)}
                                                    className="bg-white border border-stone-200 px-4 py-2 text-xs uppercase tracking-wider font-bold text-stone-600 hover:text-gold-600 hover:border-gold-400 transition-colors rounded-sm flex items-center gap-2"
                                                >
                                                    {expandedOrder === order.id ? 'Hide Details' : 'View Details'}
                                                    {expandedOrder === order.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                                                </button>
                                                <button 
                                                    onClick={() => downloadInvoice(order)}
                                                    className="bg-white border border-stone-200 p-2 text-stone-500 hover:text-stone-900 rounded-sm flex items-center gap-1" 
                                                    title="Download Invoice"
                                                >
                                                    <Download size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="p-6">
                                            <div className="flex flex-col md:flex-row gap-6">
                                                <div className="md:w-1/4">
                                                    <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-2">Status</h4>
                                                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${getStatusStyle(order.status)}`}>
                                                        {getStatusIcon(order.status)}
                                                        {isComplete ? 'Order Complete' : getDisplayStatus(order.status)}
                                                    </span>
                                                    
                                                    {!isPickup && (
                                                        <div className="mt-2">
                                                            <div className="flex items-center gap-1 text-[9px] text-blue-600 bg-blue-50 px-2 py-1 rounded-sm border border-blue-100 w-fit">
                                                                <Truck size={10} className="text-blue-600" />
                                                                <span>Shipping</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {isPickup && (
                                                        <div className="mt-3 p-2 bg-amber-50 rounded-sm border border-amber-100">
                                                            <p className="text-[10px] font-bold text-amber-700 flex items-center gap-1">
                                                                <Store size={12} /> Pickup Order
                                                            </p>
                                                            <p className="text-[9px] text-amber-600 mt-1">
                                                                {order.status === 'Ready for Pickup' 
                                                                    ? '✅ Ready for pickup at our Jewelry Shop' 
                                                                    : order.status === 'Picked Up' 
                                                                    ? '📦 Order picked up' 
                                                                    : order.status === 'Paid'
                                                                    ? '⏳ Payment verified, awaiting processing'
                                                                    : '⏳ Awaiting payment verification'}
                                                            </p>
                                                            {order.status === 'Ready for Pickup' && (
                                                                <p className="text-[9px] text-amber-600 mt-1 font-medium">
                                                                    Please visit our Jewelry Shop to collect your order.
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <div className="mt-4">
                                                        {order.trackingNumber && !isPickup ? (
                                                            <>
                                                                <span className="text-[10px] text-stone-500 uppercase block mb-1">Tracking Number</span>
                                                                <p className="text-xs font-mono text-stone-700">{order.trackingNumber}</p>
                                                            </>
                                                        ) : !isPickup && !order.trackingNumber ? (
                                                            <div className="mt-2">
                                                                <p className="text-[10px] text-stone-400 italic flex items-center gap-1">
                                                                    <Truck size={10} className="text-stone-400" />
                                                                    Tracking number will be assigned soon
                                                                </p>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className="flex-1">
                                                    <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-4">Items</h4>
                                                    <div className="space-y-4">
                                                        {order.items && order.items.map((item, idx) => {
                                                            const itemKey = `${order.id}-${idx}`;
                                                            const imageUrl = getProductImage(item, item.id);
                                                            
                                                            return (
                                                                <div key={itemKey} className="flex gap-4 items-center group">
                                                                    <div 
                                                                        className="w-16 h-16 bg-stone-100 rounded-sm overflow-hidden border border-stone-200 flex-shrink-0 cursor-zoom-in"
                                                                        onClick={() => openLightbox(imageUrl)}
                                                                    >
                                                                        <img 
                                                                            src={imageUrl} 
                                                                            alt={item.name || 'Product'} 
                                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                                            onError={(e) => {
                                                                                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    
                                                                    <div className="flex-1">
                                                                        <p className="text-sm font-serif text-stone-900">{item.name || item.product_name || 'Product'}</p>
                                                                        <p className="text-xs text-stone-500">Qty: {item.quantity || 1} • {formatCurrency(item.price || item.product_price)}</p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="md:w-1/5 flex flex-col gap-2 justify-center border-t md:border-t-0 md:border-l border-stone-100 pt-4 md:pt-0 md:pl-6">
                                                    <button 
                                                        onClick={() => setTrackingModalOrder(order)}
                                                        className="w-full bg-stone-900 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-gold-600 transition-colors shadow-sm rounded-sm flex items-center justify-center gap-2"
                                                    >
                                                        <Truck size={14} />
                                                        Track Order
                                                    </button>
                                                    
                                                    {isPickup && order.status === 'Ready for Pickup' && (
                                                        <div className="text-center p-2 bg-amber-50 border border-amber-200 rounded-sm">
                                                            <Store size={16} className="mx-auto text-amber-600 mb-1" />
                                                            <p className="text-[9px] text-amber-700">Ready for pickup</p>
                                                        </div>
                                                    )}
                                                    
                                                    {isPickup && order.status !== 'Ready for Pickup' && order.status !== 'Picked Up' && (
                                                        <div className="text-center p-2 bg-amber-50/50 border border-amber-100 rounded-sm">
                                                            <Store size={16} className="mx-auto text-amber-500 mb-1" />
                                                            <p className="text-[9px] text-amber-600">Pickup order</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {expandedOrder === order.id && (
                                                <div className="mt-6 pt-6 border-t border-stone-100 grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                                                    <div>
                                                        <h5 className="text-xs font-bold text-stone-900 mb-2">
                                                            {isPickup ? 'Pickup Information' : 'Shipping Address'}
                                                        </h5>
                                                        {isPickup ? (
                                                            <div className="text-sm text-stone-600 leading-relaxed">
                                                                <p className="font-medium">Pickup Person:</p>
                                                                <p>{orderAny.pickup_name || order.customerName || `${profileData.firstName} ${profileData.lastName}`}</p>
                                                                <p className="mt-2 font-medium">Contact:</p>
                                                                <p>{orderAny.pickup_phone || orderAny.customer_phone || profileData.phone}</p>
                                                                <p className="mt-2 font-medium">Location:</p>
                                                                <p>Sparkling Gems Jewelry Shop<br />2F, San Antonio Building, Corumi, Quezon City</p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-stone-600 leading-relaxed">
                                                                {order.customerName || orderAny.customer_name || `${profileData.firstName} ${profileData.lastName}`}<br />
                                                                {order.shippingAddress || orderAny.shipping_address || profileData.address}<br />
                                                                {profileData.city}, {profileData.postalCode}<br />
                                                                {orderAny.customer_phone || profileData.phone}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h5 className="text-xs font-bold text-stone-900 mb-2">Payment Information</h5>
                                                        <div className="flex items-center gap-2 text-sm text-stone-600 mb-1">
                                                            {(order.paymentMethod === 'Bank Transfer' || orderAny.payment_method === 'Bank Transfer') ? 
                                                                <span className="text-gold-600 font-bold text-lg">₱</span> : 
                                                                <CreditCard size={16} className="text-stone-400" />
                                                            }
                                                            {order.paymentMethod || orderAny.payment_method || 'Bank Transfer'}
                                                        </div>
                                                        <p className="text-xs text-stone-400">Total: ₱{(order.totalAmount || order.total || 0).toLocaleString()}</p>
                                                        {order.transactionId && (
                                                            <p className="text-xs text-stone-400 mt-1">Transaction: {order.transactionId}</p>
                                                        )}
                                                        {orderAny.transaction_id && !order.transactionId && (
                                                            <p className="text-xs text-stone-400 mt-1">Transaction: {orderAny.transaction_id}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-stone-50 rounded-sm border border-stone-100">
                            <ShoppingBag className="mx-auto text-stone-300 mb-6" size={64} />
                            <p className="text-stone-500 font-serif text-xl mb-2">No orders found.</p>
                            <p className="text-stone-400 text-sm mb-8">You haven't placed any orders yet, or no orders match your search.</p>
                            <button onClick={() => onNavigate('shop')} className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors">
                                Start Shopping
                            </button>
                        </div>
                    )}
                </div>
            );

        case 'wishlist':
            return (
                <div className="space-y-6 animate-fade-in">
                     <div className="flex justify-between items-center border-b border-stone-200 pb-4">
                        <h2 className="font-serif text-2xl text-stone-900">My Collection Wishlist</h2>
                        <div className="text-sm text-stone-500">{wishlist.length} Items</div>
                     </div>

                     {wishlist.length > 0 ? (
                        <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-stone-100 bg-stone-50/50 flex justify-between items-center">
                                <button 
                                    onClick={() => onToggleAllWishlistSelection && onToggleAllWishlistSelection(!isAllSelected)}
                                    className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-stone-600 hover:text-gold-600 transition-colors"
                                >
                                    {isAllSelected ? <CheckSquare size={16} className="text-gold-600"/> : <Square size={16}/>}
                                    Select All
                                </button>
                                
                                {selectedWishlistItems.length > 0 && (
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={handleBulkDelete}
                                            className="flex items-center gap-1 text-xs uppercase tracking-wider text-red-500 hover:text-red-700 transition-colors"
                                        >
                                            <Trash2 size={14} /> Delete Selected
                                        </button>
                                        <div className="h-4 w-px bg-stone-300"></div>
                                        <button 
                                            onClick={handleBulkMoveToCart}
                                            className="flex items-center gap-1 text-xs uppercase tracking-wider text-stone-900 hover:text-gold-600 transition-colors font-bold"
                                        >
                                            <ShoppingBag size={14} /> Add to Bag
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="divide-y divide-stone-100">
                                {wishlist.map((item) => {
                                    const productImage = getProductImage(item, item.id);
                                    
                                    return (
                                        <div key={item.id} className="p-6 flex flex-col md:flex-row items-center gap-6 hover:bg-stone-50/30 transition-colors group">
                                            <div className="flex items-center gap-4 w-full md:w-auto">
                                                <button 
                                                    onClick={() => onToggleWishlistSelection && onToggleWishlistSelection(item.id)}
                                                    className="text-stone-400 hover:text-gold-600 transition-colors"
                                                >
                                                    {item.selected ? <CheckSquare size={20} className="text-gold-600"/> : <Square size={20}/>}
                                                </button>
                                                <div 
                                                    onClick={() => onSelectProduct && onSelectProduct(item)}
                                                    className="w-24 h-24 bg-stone-100 border border-stone-200 overflow-hidden cursor-pointer relative rounded-sm"
                                                >
                                                    <img 
                                                        src={wishlistImageErrors[item.id] ? PLACEHOLDER_IMAGE : productImage} 
                                                        alt={item.name} 
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                        onError={() => handleWishlistImageError(item.id)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex-1 text-center md:text-left">
                                                <h3 
                                                    onClick={() => onSelectProduct && onSelectProduct(item)}
                                                    className="font-serif text-lg text-stone-900 cursor-pointer hover:text-gold-600 transition-colors"
                                                >
                                                    {item.name}
                                                </h3>
                                                <p className="text-xs text-stone-500 mb-2">{item.description}</p>
                                                <span className="text-sm font-bold text-stone-900">{formatCurrency(item.price)}</span>
                                            </div>

                                            <div className="flex flex-col md:flex-row items-center gap-6">
                                                <div className="flex items-center border border-stone-200 rounded-sm">
                                                    <button 
                                                        onClick={() => onUpdateWishlistQuantity && onUpdateWishlistQuantity(item.id, -1)}
                                                        className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className="w-10 text-center text-sm font-mono text-stone-900">{item.quantity || 1}</span>
                                                    <button 
                                                        onClick={() => onUpdateWishlistQuantity && onUpdateWishlistQuantity(item.id, 1)}
                                                        className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <button 
                                                        onClick={() => onMoveToCart && onMoveToCart([item.id])}
                                                        className="bg-stone-900 text-white px-4 py-2 text-[10px] uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm"
                                                    >
                                                        Add to Bag
                                                    </button>
                                                    <button 
                                                        onClick={() => onRemoveFromWishlist && onRemoveFromWishlist([item.id])}
                                                        className="p-2 border border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-200 rounded-sm transition-colors"
                                                        title="Remove"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                     ) : (
                        <div className="text-center py-12 bg-stone-50 rounded-sm border border-stone-100">
                            <Heart className="mx-auto text-stone-300 mb-4" size={48} />
                            <p className="text-stone-500 font-serif text-lg">Your wishlist is empty.</p>
                            <button onClick={() => onNavigate('shop')} className="mt-4 text-xs uppercase font-bold text-gold-600 hover:text-gold-500 underline">Browse Collections</button>
                        </div>
                     )}
                </div>
            );

        case 'settings':
            return (
                <div className="space-y-8 animate-fade-in">
                    {isLoadingProfile ? (
                        <div className="flex justify-center py-12">
                            <Loader size={32} className="animate-spin text-gold-600" />
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-center border-b border-stone-200 pb-4">
                                <h2 className="font-serif text-2xl text-stone-900">Account Settings</h2>
                                <button 
                                    onClick={() => {
                                        if (isEditing) {
                                            const form = document.getElementById('settings-form');
                                            if (form) {
                                                const event = new Event('submit', { cancelable: true, bubbles: true });
                                                form.dispatchEvent(event);
                                            }
                                        } else {
                                            setIsEditing(true);
                                        }
                                    }}
                                    className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-600 hover:text-gold-500 font-bold bg-gold-50 px-4 py-2 rounded-sm border border-gold-100 hover:border-gold-300 transition-all"
                                    disabled={isUploadingPhoto}
                                >
                                    {isEditing ? (
                                        <>
                                            {isUploadingPhoto ? (
                                                <><Loader size={14} className="animate-spin" /> Saving...</>
                                            ) : (
                                                <><Save size={14} /> Save Changes</>
                                            )}
                                        </>
                                    ) : (
                                        <><Edit3 size={14} /> Edit Profile</>
                                    )}
                                </button>
                            </div>

                            <form id="settings-form" onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="md:col-span-2 bg-white p-8 border border-stone-200 shadow-sm rounded-sm">
                                    <h3 className="text-xs uppercase tracking-widest text-stone-400 font-bold mb-6 flex items-center gap-2">
                                        <User size={14} /> Personal Information
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">First Name</label>
                                            <input 
                                                type="text" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.firstName} 
                                                onChange={(e) => setProfileData({...profileData, firstName: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">Last Name</label>
                                            <input 
                                                type="text" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.lastName} 
                                                onChange={(e) => setProfileData({...profileData, lastName: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">Email</label>
                                            <input 
                                                type="email" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.email} 
                                                onChange={(e) => setProfileData({...profileData, email: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">Phone</label>
                                            <input 
                                                type="text" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.phone} 
                                                onChange={(e) => setProfileData({...profileData, phone: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">Address</label>
                                            <input 
                                                type="text" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.address} 
                                                onChange={(e) => setProfileData({...profileData, address: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase">City</label>
                                            <input 
                                                type="text" 
                                                disabled={!isEditing || isUploadingPhoto} 
                                                value={profileData.city} 
                                                onChange={(e) => setProfileData({...profileData, city: e.target.value})} 
                                                className="w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors" 
                                            />
                                        </div>
                                    </div>
                                    
                                    {saveSuccess && (
                                        <div className="mt-4 p-2 bg-green-50 border border-green-200 text-green-700 text-xs rounded-sm">
                                            Profile updated successfully!
                                        </div>
                                    )}
                                </div>
                            </form>
                        </>
                    )}
                </div>
            );
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-20">
      
      {/* Tier Info Modal */}
      {showTierInfoModal && <TierInfoModal />}
      
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[400] bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full transition-colors">
            <XCircle size={30} />
          </button>
          <img
            src={lightboxImage}
            alt="Full size view"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
            }}
          />
        </div>
      )}
      
      {showPhotoUploadModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" onClick={() => setShowPhotoUploadModal(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]">
            <div className="bg-stone-900 text-white p-6 flex justify-between items-center">
              <div>
                <h3 className="font-serif text-xl">Update Profile Photo</h3>
                <p className="text-xs text-stone-400">Choose a new profile picture (Max 20MB)</p>
              </div>
              <button onClick={() => setShowPhotoUploadModal(false)} className="text-stone-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handlePhotoSelect}
                accept="image/*"
                className="hidden"
              />
              
              <div className="flex flex-col items-center">
                <div className="w-40 h-40 rounded-full border-4 border-gold-200 overflow-hidden shadow-xl mb-4">
                  <img 
                    key={profileData.avatar}
                    src={selectedPhoto || profileData.avatar || PLACEHOLDER_IMAGE} 
                    alt="Profile Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                    }}
                  />
                </div>
                
                {selectedPhoto ? (
                  <p className="text-xs text-green-600 font-bold">New photo selected</p>
                ) : (
                  <p className="text-xs text-stone-400">Current profile photo</p>
                )}
              </div>
              
              <div className="flex flex-col gap-3">
                <button 
                  onClick={triggerFileInput}
                  className="w-full border-2 border-dashed border-stone-300 py-4 px-4 rounded-sm hover:border-gold-500 hover:bg-stone-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Camera size={18} className="text-stone-400" />
                  <span className="text-sm font-medium">Choose New Photo</span>
                </button>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowPhotoUploadModal(false)}
                    className="flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handlePhotoUpload}
                    disabled={!selectedPhoto || isUploadingPhoto}
                    className="flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isUploadingPhoto ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      'Upload Photo'
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-stone-400 text-center mt-2">Supported formats: JPG, PNG, GIF, WEBP (Max 20MB)</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showPaymentModal && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center">
              <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" onClick={() => setShowPaymentModal(false)}></div>
              <div className="relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]">
                  <div className="bg-stone-900 text-white p-6 flex justify-between items-center">
                      <div>
                          <h3 className="font-serif text-xl">Submit Payment Proof</h3>
                          <p className="text-xs text-stone-400">Verify your bank transfer</p>
                      </div>
                      <button onClick={() => setShowPaymentModal(false)} className="text-stone-400 hover:text-white"><X size={20} /></button>
                  </div>
                  
                  <form onSubmit={handleSubmitPayment} className="p-8 space-y-6">
                      <div>
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Select Order</label>
                          <select 
                              value={paymentForm.orderId}
                              onChange={(e) => setPaymentForm({...paymentForm, orderId: e.target.value})}
                              required
                              className="w-full border border-stone-300 p-3 rounded-sm text-sm focus:border-gold-500 outline-none bg-white text-stone-900"
                          >
                              <option value="" disabled>Select a pending order...</option>
                              {pendingOrders.map(order => {
                                  const orderAny = order as any;
                                  return (
                                      <option key={order.id} value={order.id}>
                                          {orderAny.order_number || order.id} — {order.items && order.items[0]?.name} {order.items && order.items.length > 1 ? `+${order.items.length - 1} more` : ''} — {formatCurrency(order.total)}
                                      </option>
                                  );
                              })}
                          </select>
                      </div>

                      <div>
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Transaction Reference No.</label>
                          <input 
                              type="text"
                              required
                              value={paymentForm.transactionRef}
                              onChange={(e) => setPaymentForm({...paymentForm, transactionRef: e.target.value})}
                              placeholder="e.g. TR-123456789"
                              className="w-full border border-stone-300 p-3 rounded-sm text-sm focus:border-gold-500 outline-none bg-white text-stone-900"
                          />
                      </div>

                      <div>
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Upload Screenshot</label>
                          <div className="flex gap-4 items-start">
                              <label className="flex-1 cursor-pointer border-2 border-dashed border-stone-300 rounded-sm p-4 flex flex-col items-center justify-center hover:border-gold-500 hover:bg-stone-50 transition-colors h-32">
                                  <Upload size={24} className="text-stone-400 mb-2" />
                                  <span className="text-xs text-stone-500 font-medium">Click to upload image</span>
                                  <input 
                                      type="file" 
                                      required
                                      accept="image/*"
                                      className="hidden"
                                      onChange={handleModalFileChange}
                                  />
                              </label>
                              {paymentForm.proofPreview && (
                                  <div className="w-32 h-32 border border-stone-200 rounded-sm overflow-hidden bg-stone-100 flex-shrink-0 relative">
                                      <img src={paymentForm.proofPreview} alt="Preview" className="w-full h-full object-cover" />
                                      <button 
                                          type="button"
                                          onClick={() => setPaymentForm({...paymentForm, proofPreview: null})}
                                          className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:text-red-500"
                                      >
                                          <X size={12} />
                                      </button>
                                  </div>
                              )}
                          </div>
                      </div>

                      <button 
                          type="submit"
                          disabled={!paymentForm.orderId || !paymentForm.transactionRef || !paymentForm.proofPreview || paymentForm.isUploading}
                          className="w-full bg-stone-900 text-white py-3 uppercase text-xs tracking-widest hover:bg-gold-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                          {paymentForm.isUploading ? (
                              <><Loader size={16} className="animate-spin" /> Uploading...</>
                          ) : (
                              'Submit Verification'
                          )}
                      </button>
                  </form>
              </div>
          </div>
      )}

      {trackingModalOrder && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center">
              <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" onClick={() => setTrackingModalOrder(null)}></div>
              <div className="relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]">
                  
                  <div className="bg-stone-900 text-white p-6 relative">
                      <button onClick={() => setTrackingModalOrder(null)} className="absolute top-4 right-4 text-stone-400 hover:text-white">
                          <X size={20} />
                      </button>
                      <h3 className="font-serif text-2xl mb-1">Track Order</h3>
                      <p className="text-stone-400 text-xs uppercase tracking-widest">Order #{trackingModalOrder.id}</p>
                      
                      {orderDetails && orderDetails.status && (
                        <div className="mt-2 inline-block px-2 py-1 bg-gold-100 text-gold-800 rounded-sm text-[10px] font-bold">
                          Current Status: {getStatusDisplayText(orderDetails.status)}
                        </div>
                      )}
                  </div>

                  <div className="p-8">
                      {/* Timeline Progress Bar - Auto lights up based on current status */}
                      <div className="flex justify-between items-center mb-8 relative px-4">
                          <div className="absolute top-1/2 left-0 right-0 h-px bg-stone-200 -z-10"></div>
                          {['Pending', 'Preparing', 'Shipped', 'Delivered'].map((step, idx) => {
                              const steps = ['Pending', 'Preparing', 'Shipped', 'Delivered'];
                              
                              let currentStatus = orderDetails?.status || trackingModalOrder.status || 'Pending';
                              if (currentStatus === 'Processing') currentStatus = 'Preparing';
                              
                              const currentIdx = steps.indexOf(currentStatus);
                              const stepIdx = steps.indexOf(step);
                              const isCompleted = stepIdx <= currentIdx;
                              const isCurrent = stepIdx === currentIdx;
                              
                              // Status-specific colors for the circles
                              let circleBg = '';
                              if (isCompleted) {
                                  if (step === 'Pending') circleBg = 'bg-orange-600';
                                  else if (step === 'Preparing') circleBg = 'bg-purple-600';
                                  else if (step === 'Shipped') circleBg = 'bg-blue-600';
                                  else if (step === 'Delivered') circleBg = 'bg-green-600';
                                  else circleBg = 'bg-gold-600';
                              } else {
                                  circleBg = 'bg-stone-300';
                              }
                              
                              return (
                                  <div key={step} className="flex flex-col items-center gap-2 bg-white px-2 z-10 flex-1">
                                      <div className={`w-8 h-8 rounded-full ${circleBg} ring-2 ${isCompleted ? 'ring-gold-300' : 'ring-stone-200'} border-2 border-white shadow-md flex items-center justify-center`}>
                                          {isCompleted && stepIdx < currentIdx && (
                                              <CheckCircle2 size={14} className="text-white" />
                                          )}
                                          {isCurrent && (
                                              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                                          )}
                                      </div>
                                      <span className={`text-[10px] uppercase font-bold ${
                                          isCurrent ? 'text-gold-600' : 
                                          isCompleted ? 'text-stone-800' : 'text-stone-300'
                                      }`}>
                                          {step}
                                      </span>
                                  </div>
                              );
                          })}
                      </div>

                      <div className="bg-stone-50 p-4 border border-stone-200 rounded-sm mb-8 flex justify-between items-center">
                          <div>
                              <span className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Tracking Number</span>
                              <span className="font-mono text-stone-900 text-sm">
                                {orderDetails?.trackingNumber || trackingModalOrder.trackingNumber || (trackingModalOrder as any).tracking_number || "Pending Assignment"}
                              </span>
                          </div>
                          <Truck className="text-stone-300" size={24} />
                      </div>

                      <div className="space-y-6 relative border-l border-stone-200 ml-3 pl-8 max-h-96 overflow-y-auto">
                          {isLoadingTimeline ? (
                              <div className="flex justify-center py-8">
                                  <div className="w-8 h-8 border-4 border-gold-600 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                          ) : orderTimeline && orderTimeline.length > 0 ? (
                              orderTimeline.map((event: any, idx: number) => {
                                  const imageUrl = getTimelineImageUrl(event);
                                  const hasImage = imageUrl && imageUrl !== PLACEHOLDER_IMAGE;
                                  
                                  // Determine status color for the timeline event
                                  const eventStatus = event.status || 'Update';
                                  const isRejected = eventStatus === 'Rejected';
                                  const isPaid = eventStatus === 'Paid';
                                  const isDelivered = eventStatus === 'Delivered' || eventStatus === 'Picked Up';
                                  const isShipped = eventStatus === 'Shipped';
                                  const isPreparing = eventStatus === 'Preparing' || eventStatus === 'Processing';
                                  const isPending = eventStatus === 'Pending';
                                  
                                  let circleColor = 'bg-stone-300';
                                  let ringColor = 'ring-stone-200';
                                  let statusBadgeClass = '';
                                  let statusTextColor = '';
                                  
                                  if (isRejected) {
                                      circleColor = 'bg-red-600';
                                      ringColor = 'ring-red-300';
                                      statusBadgeClass = 'bg-red-100 text-red-700 border-red-200';
                                      statusTextColor = 'text-red-700';
                                  } else if (isPaid) {
                                      circleColor = 'bg-emerald-600';
                                      ringColor = 'ring-emerald-300';
                                      statusBadgeClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
                                      statusTextColor = 'text-emerald-700';
                                  } else if (isDelivered) {
                                      circleColor = 'bg-green-600';
                                      ringColor = 'ring-green-300';
                                      statusBadgeClass = 'bg-green-100 text-green-700 border-green-200';
                                      statusTextColor = 'text-green-700';
                                  } else if (isShipped) {
                                      circleColor = 'bg-blue-600';
                                      ringColor = 'ring-blue-300';
                                      statusBadgeClass = 'bg-blue-100 text-blue-700 border-blue-200';
                                      statusTextColor = 'text-blue-700';
                                  } else if (isPreparing) {
                                      circleColor = 'bg-purple-600';
                                      ringColor = 'ring-purple-300';
                                      statusBadgeClass = 'bg-purple-100 text-purple-700 border-purple-200';
                                      statusTextColor = 'text-purple-700';
                                  } else if (isPending) {
                                      circleColor = 'bg-orange-600';
                                      ringColor = 'ring-orange-300';
                                      statusBadgeClass = 'bg-orange-100 text-orange-700 border-orange-200';
                                      statusTextColor = 'text-orange-700';
                                  }
                                  
                                  return (
                                      <div key={idx} className="relative pb-6">
                                          {/* Timeline connector line between items */}
                                          {idx !== orderTimeline.length - 1 && (
                                              <div className="absolute left-[-9px] top-6 bottom-0 w-0.5 bg-stone-200"></div>
                                          )}
                                          
                                          {/* Circle marker - Full circle with proper styling */}
                                          <div className="absolute -left-[37px] top-1">
                                              <div className={`w-4 h-4 rounded-full ${circleColor} ring-2 ${ringColor} border-2 border-white shadow-sm`}></div>
                                          </div>
                                          
                                          {/* Content */}
                                          <div className="ml-0">
                                              {/* Status Badge */}
                                              <div className="flex items-center gap-2 mb-1">
                                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${statusBadgeClass}`}>
                                                      {eventStatus === 'Processing' ? 'Preparing' : (eventStatus || 'Update')}
                                                  </span>
                                              </div>
                                              
                                              <p className={`text-sm ${statusTextColor} mb-1`}>{event.description || event.notes || 'No description'}</p>
                                              
                                              {hasImage && (
                                                  <div 
                                                      className="mt-2 mb-2 w-full max-w-[200px] h-32 border border-stone-200 rounded-sm overflow-hidden relative group cursor-pointer"
                                                      onClick={() => openLightbox(imageUrl)}
                                                  >
                                                      <img 
                                                        src={imageUrl}
                                                        alt="Proof" 
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                                        }}
                                                      />
                                                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                          <Eye size={24} className="text-white" />
                                                      </div>
                                                  </div>
                                              )}

                                              <div className="flex gap-3 text-[10px] text-stone-400">
                                                  <span>{new Date(event.created_at).toLocaleDateString()} • {new Date(event.created_at).toLocaleTimeString()}</span>
                                                  {event.location && event.location !== 'Admin Portal' && event.location !== 'admin' && (
                                                    <span>• {event.location}</span>
                                                  )}
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })
                          ) : (
                              <div className="relative">
                                  <div className="absolute -left-[37px] top-1 w-4 h-4 rounded-full bg-stone-300 border-2 border-white"></div>
                                  <div className="ml-0">
                                      <p className="text-sm text-stone-500 italic">Order placed. Awaiting updates.</p>
                                  </div>
                              </div>
                          )}
                      </div>
                  </div>
                  
                  <div className="bg-stone-50 p-4 border-t border-stone-100 text-center">
                      <p className="text-[10px] text-stone-400">Estimated Delivery: 3-5 Business Days from Shipment</p>
                  </div>
              </div>
          </div>
      )}

      <div className="relative h-72 md:h-96 overflow-hidden bg-stone-900 group">
        <div className="absolute inset-0">
             <img 
               src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop" 
               className="w-full h-full object-cover opacity-30 animate-[zoomSlow_30s_infinite_alternate]"
               alt="Header Background"
               onError={(e) => {
                 (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
               }}
             />
             <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 w-full p-6 md:p-12 max-w-7xl mx-auto flex items-end">
             <div className="flex items-end gap-8">
                 <div 
                    className="w-28 h-28 md:w-40 md:h-40 rounded-full border-[6px] border-stone-50 bg-stone-200 shadow-2xl overflow-hidden relative group/avatar cursor-pointer"
                    onClick={handlePhotoClick}
                 >
                    <img 
                        key={profileData.avatar}
                        src={profileData.avatar || PLACEHOLDER_IMAGE} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover/avatar:scale-110" 
                        alt="Profile" 
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                        }}
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity">
                        <Camera className="text-white" size={28} />
                    </div>
                 </div>
                 <div className="mb-6">
                     <h1 className="font-serif text-3xl md:text-5xl text-white mb-2">{profileData.firstName} {profileData.lastName}</h1>
                     <div className="flex flex-wrap items-center gap-3 text-stone-300 text-sm">
                        <span className="bg-gold-600 text-white px-3 py-1 rounded text-[10px] uppercase font-bold tracking-widest shadow-lg shadow-gold-900/20">{tierDetails.tierName} Member</span>
                        <span>•</span>
                        <span>Member since {memberSinceDate}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1"><MapPin size={12}/> {profileData.city}</span>
                     </div>
                 </div>
             </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col lg:flex-row gap-12">
        
        <aside className="lg:w-64 flex-shrink-0">
            <nav className="space-y-1 sticky top-32">
                {[
                    { id: 'overview', label: 'Overview', icon: <User size={18} /> },
                    { id: 'orders', label: 'Order History', icon: <Package size={18} /> },
                    { id: 'payment-proof', label: 'Payment Proof', icon: <Upload size={18} /> },
                    { id: 'wishlist', label: 'My Collection', icon: <Heart size={18} /> },
                    { id: 'settings', label: 'Settings', icon: <Settings size={18} /> }
                ].map((item) => (
                    <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id as any)}
                        className={`w-full flex items-center justify-between px-5 py-4 rounded-sm transition-all text-sm font-medium border-l-4 ${
                            activeTab === item.id 
                            ? 'bg-white text-gold-600 shadow-sm border-gold-600' 
                            : 'border-transparent text-stone-500 hover:bg-white hover:text-stone-900'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            {item.icon}
                            <span>{item.label}</span>
                        </div>
                        {activeTab === item.id && <ChevronRight size={14} />}
                    </button>
                ))}
                
                <div className="pt-8 mt-8 border-t border-stone-200">
                    <button onClick={onLogout} className="w-full flex items-center gap-3 px-5 py-4 text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors group">
                        <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" />
                        <span>Sign Out</span>
                    </button>
                </div>
            </nav>
        </aside>

        <main className="flex-1 min-h-[500px]">
            {renderTabContent()}
        </main>

      </div>
    </div>
  );
};

export default MemberProfile;